import { useState } from "react";
import SectionHeading from "@/components/SectionHeading";
import HostApplyModal from "@/components/HostApplyModal";

/**
 * Second, open tier of the Vibecity program.
 *
 * The 10-city Lead flagship above is invite-quality. Host is open to anyone
 * running a community event anywhere in the world. Emergent backs successful
 * hosts with credits, a playbook, brand assets, and amplification.
 */
export default function HostSection() {
    const [open, setOpen] = useState(false);

    return (
        <section
            id="host"
            data-testid="host-section"
            className="relative py-24 md:py-32 border-t border-line"
            style={{ background: "#0a0a0a" }}
        >
            <div className="max-w-6xl mx-auto px-6">
                <SectionHeading
                    lineA="Host An Event."
                    lineB="Anywhere."
                    accent="green"
                    testId="host-heading"
                    subtitle="Not lucky enough to be in one of our anchor cities? Run your own community event and we back it."
                />

                {/* Example events strip */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-16">
                    {EXAMPLES.map((e) => (
                        <ExampleCard key={e.title} {...e} />
                    ))}
                </div>

                {/* Two-column: what hosts get / qualification bar */}
                <div className="grid md:grid-cols-2 gap-6 mb-16">
                    <BackedByPanel />
                    <QualifyPanel />
                </div>

                <div className="flex flex-col items-center gap-4">
                    <button
                        type="button"
                        onClick={() => setOpen(true)}
                        data-testid="host-cta"
                        className="inline-flex items-center justify-center hover:brightness-110 transition-[filter]"
                        style={{
                            padding: "14px 36px",
                            minWidth: 260,
                            height: 56,
                            background:
                                "linear-gradient(180deg, #EDE9C8 11.84%, #807F6F 138.81%)",
                            boxShadow:
                                "inset 0 4px 7.1px #FFFFFF, inset 0 -1px 15.1px #FFFFFF",
                            borderRadius: 14,
                            fontFamily:
                                '"Figtree", "Inter", system-ui, sans-serif',
                            fontWeight: 700,
                            fontSize: 16,
                            letterSpacing: "0.05em",
                            textTransform: "uppercase",
                            color: "#0E100F",
                            textDecoration: "none",
                            cursor: "pointer",
                            border: 0,
                        }}
                    >
                        Apply to Host
                    </button>
                    <p className="text-[11px] tracking-[0.22em] uppercase text-ink-soft font-display">
                        Reviewed within 3 business days.
                    </p>
                </div>
            </div>

            {open && <HostApplyModal onClose={() => setOpen(false)} />}
        </section>
    );
}

const EXAMPLES = [
    {
        title: "Women's Hackathon",
        city: "Scottsdale, AZ",
        blurb: "Build a scheduling tool for local caregivers in 48 hours.",
        accent: "#90FDA6",
    },
    {
        title: "Sushi App Build Night",
        city: "Osaka, Japan",
        blurb: "Ship a sushi-tracker before the last omakase order.",
        accent: "#F8FF28",
    },
    {
        title: "Devs & Distilleries",
        city: "Edinburgh, UK",
        blurb: "Pitch AI-native side projects between whisky flights.",
        accent: "#07E6F6",
    },
];

function ExampleCard({ title, city, blurb, accent }) {
    return (
        <div
            data-testid={`host-example-${title.replace(/\s+/g, "-").toLowerCase()}`}
            className="border border-line rounded-2xl p-6 flex flex-col justify-between hover:border-ink transition-colors"
            style={{ background: "rgba(255,255,255,0.02)" }}
        >
            <div
                className="text-[10px] tracking-[0.28em] uppercase font-display font-bold"
                style={{ color: accent }}
            >
                {city}
            </div>
            <div>
                <h3
                    className="font-display font-black mt-4"
                    style={{
                        fontSize: "clamp(1.35rem, 2vw, 1.75rem)",
                        lineHeight: 1.05,
                        color: "#FFFCE1",
                    }}
                >
                    {title}
                </h3>
                <p className="mt-2 text-sm text-ink-soft leading-relaxed">
                    {blurb}
                </p>
            </div>
        </div>
    );
}

function BackedByPanel() {
    return (
        <div className="border border-line rounded-2xl p-6 md:p-8">
            <div className="vc-section-label text-[#5fe3c9] mb-4">
                What you get
            </div>
            <ul className="space-y-3 text-ink text-[0.95rem]">
                {[
                    "Emergent credits for every attendee",
                    "The Vibecity playbook - run of show + comms + judging",
                    "Brand kit: badges, banners, socials, event page",
                    "Amplification from Emergent socials + newsletter",
                ].map((it) => (
                    <li
                        key={it}
                        className="flex gap-3 items-start"
                        data-testid={`host-perk-${it.slice(0, 6)}`}
                    >
                        <span
                            aria-hidden
                            style={{
                                marginTop: 6,
                                width: 6,
                                height: 6,
                                borderRadius: 999,
                                background: "#5fe3c9",
                                flexShrink: 0,
                            }}
                        />
                        {it}
                    </li>
                ))}
            </ul>
        </div>
    );
}

function QualifyPanel() {
    return (
        <div className="border border-line rounded-2xl p-6 md:p-8">
            <div className="vc-section-label text-[#F8FF28] mb-4">
                To qualify
            </div>
            <ul className="space-y-4">
                {[
                    ["Builders as audience", "Founders, engineers, designers - anyone shipping."],
                    ["30+ attendees", "Small enough to know names. Big enough to be a room."],
                    ["2+ weeks out", "So we have time to help you fill the room."],
                ].map(([t, b]) => (
                    <li key={t}>
                        <div className="font-display font-bold text-ink text-base">
                            {t}
                        </div>
                        <div className="text-sm text-ink-soft mt-0.5">{b}</div>
                    </li>
                ))}
            </ul>
        </div>
    );
}
